<template>
  <div class="video-player" :class="{['type-' + type]: type}" ref="videoRef">
    <div v-if= "showPoster">
      <img
        :class="{'flip-h': flipH}"
        :src="linkToPoster"
      />
      <button class="play-button" @click="play()" />
    </div>

    <video v-else
      ref="video"
      :class="{'flip-h': flipH}"
      @timeupdate="timeUpdate"
      @pause="onPause"
      @play="onPlay"
      @click="onVideoClick"
      @canplay="onCanPlay"
      @loadedmetadata="onCanPlay"
      playsinline
      autoplay
    >
      <source :src="linkToVideo"/>
    </video>

    <teleport :disabled="!modal" to="body">
      <transition name="fade">
        <template v-if="showModal">
            <div class="video-player-modal" @click="onNestedPlayerClosed">
              <VideoPlayer :src="src" type="large" :modal="false" :isShowPoster="false" :flip-h="flipH" :is-close-visible="true" @close="onNestedPlayerClosed" />
            </div>

        </template>
      </transition>
    </teleport>

    <div class="close" v-if="isCloseVisible" @click="close()">
      <i class="pi pi-times"></i>
    </div>

    <transition name="fade">
      <button type="button" class="play-button" @click="play" v-if="canPlay && !isPlaying" />
    </transition>

    <transition name="fade">
      <button type="button" class="sound" @click="toggleSound" v-if="isPlaying">
        <i class="pi" :class="{'pi-volume-up': muted, 'pi-volume-off': !muted}"></i>
      </button>
    </transition>

    <transition name="fade">
      <div class="progress" @click="changeProgress" v-if="isPlaying">
        <div class="progress-value" :style="{width: progressValue + '%'}"></div>
      </div>
    </transition>

    <div class="title">
      <slot name="title" />
    </div>

    <slot />
  </div>
</template>

<script setup>
import {ref, onMounted, toRefs, watch} from "vue";
import Loader from "../assets/images/loader.svg";

const emit = defineEmits(['isPlaying', 'play', 'close'])
const props = defineProps({
  type: {type: String, validator: (type) => ['small', 'large', 'fluid'].includes(type)},
  modal: {type: Boolean, default: false},
  src: {type: [String, File, Object], required: true},
  isCloseVisible: {type: Boolean, default: false},
  flipH: {type: Boolean, default: false},
  isShowPoster: {type: Boolean, default: true},
  autoPlay: {type: Boolean, default: true},
  height: {type: Number, default: 560},
});
const {type, modal, src, isShowPoster, autoPlay} = toRefs(props);

const flipH = ref();
const canPlay = ref(false);
const isPlaying = ref(false);
const muted = ref(false);
const progressValue = ref(0);
const videoRef = ref();
const video = ref();
const showModal = ref(false);
const showPoster = ref(isShowPoster.value);
const linkToVideo = ref();
const linkToPoster = ref();

const convertUrl = (url, type) => {
  const width = props.height === 350 ? 280 : null;

  const poster_options = props.modal ? 'w-72,h-72' : 'w-216';
  const video_options =  props.height === 560 ? 'h-560:f-mp4,q-80' : 'h-' + props.height + (width ? ',w-' + width : '') + ':f-mp4,q-50';

  const params_url = new URL(url);
  const param = params_url.searchParams;

  param.set('tr', type === 'poster' ? poster_options : video_options);

  return  params_url.toString();
}

watch(() => videoRef.value, () => { //for modal
  video.value = videoRef.value.getElementsByTagName("video")[0] || null;

  if (video.value){
    video.value.autoplay = autoPlay.value;

    video.value.onloadeddat = () => {
        const playButton = document.querySelector('.play-button');
        playButton.addEventListener('click', function () {
          video.value.muted ^= 1;
        }, false)
    };
  }
  return video.value
});

onMounted(() => muted.value = video.value?.muted || null);

watch(() => props.flipH, () => flipH.value = props.flipH);

watch(() => src.value, () => {
  URL.revokeObjectURL(linkToVideo.value);
  flipH.value = props.flipH;

  if (src.value instanceof File) {
    linkToVideo.value = URL.createObjectURL(src.value);
    linkToVideo.value += '#t=0.001'
    linkToPoster.value = null;

  } else if (typeof src.value === 'object') { // Video model
    const videObject = src.value || {};

    linkToVideo.value = convertUrl(videObject.video_url, 'video');
    linkToPoster.value = convertUrl(videObject.poster_url, 'poster');

    if (videObject.settings) {
      flipH.value = videObject.settings.should_reflect_horizontally;
    }
  } else if (typeof src.value === 'string') {
    linkToVideo.value = src.value;
    linkToVideo.value += '#t=0.001'
    linkToPoster.value = null
  }

},{
  immediate: true
})

const play = () => {
  if (modal.value) {
    showModal.value = true;
  } else {
    if (video.value) {
      video.value.play();
    } else {
      showPoster.value = false;
    }
  }
}

const toggleSound = () => {
  video.value.muted = !video.value.muted;
  muted.value = video.value.muted;
};

const timeUpdate = () => {
  if (video.value) {
    progressValue.value = Math.floor((video.value.currentTime / video.value.duration) * 100);
    if (progressValue.value === 100 && isShowPoster.value ){
      showPoster.value = true;
    }
  }
}

const changeProgress = (e) => {
  const pos = (e.pageX  - (e.target.offsetLeft + e.target.offsetParent.offsetLeft)) / e.target.offsetWidth;

  video.value.currentTime = pos * video.value.duration;
}

const onPlay = () => isPlaying.value = true;
const onPause = () => isPlaying.value = false;
const onVideoClick = () => {
  if (isPlaying.value) {
    video.value.pause();
  }
}

const close = () => emit('close');
const onNestedPlayerClosed = () => showModal.value = false;
const onCanPlay = () => {
  canPlay.value = true;
}
</script>

<style lang="scss" scoped>
@import "../assets/sass/theme_variables";

.video-player {
  position: relative;
  width: toRem(216);
  height: toRem(385);
  //background: rgba(255, 255, 255, 0.4);
  border-radius: 10px;

  &.type-small {
    width: toRem(72);
    height: toRem(72);

    .play-button {
      width: toRem(32);
      height: toRem(32);

      &:before {
        width: toRem(10);
        height: toRem(10);
        background-size: contain;
        transform: translateX(1px);
      }
    }
  }

  &.type-large {
    width: 50vw;
    height: 50vh;
    background: #000;
  }

  &.type-fluid {
    width: 100%;
    height: 100%;
  }

  video {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;

    background-image: url(/images/loader.svg);
    background-repeat: no-repeat;
    background-size: 66px 66px;
    background-position: center;

    &.flip-h {
      transform: scaleX(-1);
    }
  }

  img {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    background: #000;
    object-fit: cover;

    &.flip-h {
      transform: scaleX(-1);
    }
  }
}

.play-button {
  display: flex;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: toRem(66);
  height: toRem(66);
  background: rgba(0, 0, 0, 0.2);
  border: 2px solid $bgBlocks;
  backdrop-filter: blur(12px);
  border-radius: 50%;
  cursor: pointer;

  &:before {
    margin: auto;
    width: toRem(17);
    height: toRem(19);
    background: url("../assets/images/play.svg") 50% 50% no-repeat;
    content: '';
    transform: translateX(2px);
  }
}

.sound {
  position: absolute;
  top: toRem(60);
  right: toRem(15);
  background: none;
  border: 0;
  color: $bgBlocks;
  padding: 0;
  cursor: pointer;
}

.progress {
  display: flex;
  position: absolute;
  left: toRem(12);
  top: toRem(12);
  right: toRem(12);
  height: 2px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
}

.progress-value {
  background-color: $bgBlocks;
  border-radius: 4px;
  transition: width 0.3s;
}

.title {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 0 toRem(12) toRem(12);
  font-weight: 600;
  color: $bgBlocks;
  font-size: toRem(16);
}

.video-player-modal {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
  z-index: 999999999;
}

.close {
  position: absolute;
  top: toRem(25);
  right: toRem(15);
  cursor: pointer;
  color: $bgBlocks;
}
</style>
